import React, { useState } from 'react';
import { useGetClientesQuery } from '../api/clientesApiSlice';
import ClienteDetalleModal from './ClienteDetalleModal';
import ClienteForm from './ClienteForm';

interface ClienteListProps {
  empresaId?: number;
}

const PAGE_SIZE = 20;

const ClienteList: React.FC<ClienteListProps> = ({ empresaId }) => {
  // Estado para filtros avanzados
  const [filtros, setFiltros] = useState({
    search: '',
    nombre: '',
    apellido: '',
    documento: '',
    localidad: '',
    telefono: '',
    empresa_id: empresaId || '',
  });
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any | null>(null);
  const [showBusqueda, setShowBusqueda] = useState(false);
  const [clienteEditar, setClienteEditar] = useState<any | null>(null);
  const [page, setPage] = useState(1);
  const [searchType, setSearchType] = useState<'contains' | 'startsWith'>('contains');

  // Construir el parámetro de búsqueda avanzada
  const searchParam = [
    filtros.search,
    filtros.nombre,
    filtros.apellido,
    filtros.documento,
    filtros.localidad
  ]
    .filter(Boolean)
    .join(' ');

  const { data, isLoading, isError } = useGetClientesQuery({
    empresa_id: filtros.empresa_id ? Number(filtros.empresa_id) : undefined,
    search: filtros.search || undefined,
    nombre: filtros.nombre || undefined,
    apellido: filtros.apellido || undefined,
    documento: filtros.documento || undefined,
    localidad: filtros.localidad || undefined,
    telefono: filtros.telefono || undefined,
    page,
    limit: PAGE_SIZE,
    searchType,
  });

  const handleFiltroChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between max-w-5xl mx-auto mb-4">
        <h3 className="text-xl font-semibold text-foreground">Listado de Clientes</h3>
        <button
          className="bg-muted text-foreground px-4 py-2 rounded-md font-semibold hover:bg-accent transition shadow"
          onClick={() => setShowBusqueda((prev) => !prev)}
        >
          {showBusqueda ? 'Ocultar búsqueda' : 'Buscar'}
        </button>
      </div>
      {showBusqueda && (
        <div className="mb-8 bg-muted/50 p-6 rounded-xl flex flex-col gap-4 shadow-md border border-border max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 w-full">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-semibold mb-1 text-muted-foreground" htmlFor="search">Búsqueda rápida</label>
              <input
                id="search"
                name="search"
                type="text"
                value={filtros.search}
                onChange={handleFiltroChange}
                className="rounded-md border px-3 py-2 w-full bg-background border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Nombre, apellido, documento..."
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-semibold mb-1 text-muted-foreground" htmlFor="nombre">Nombre</label>
              <input
                id="nombre"
                name="nombre"
                type="text"
                value={filtros.nombre}
                onChange={handleFiltroChange}
                className="rounded-md border px-3 py-2 w-full bg-background border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-semibold mb-1 text-muted-foreground" htmlFor="apellido">Apellido</label>
              <input
                id="apellido"
                name="apellido"
                type="text"
                value={filtros.apellido}
                onChange={handleFiltroChange}
                className="rounded-md border px-3 py-2 w-full bg-background border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4 w-full">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-semibold mb-1 text-muted-foreground" htmlFor="documento">Documento</label>
              <input
                id="documento"
                name="documento"
                type="text"
                value={filtros.documento}
                onChange={handleFiltroChange}
                className="rounded-md border px-3 py-2 w-full bg-background border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-semibold mb-1 text-muted-foreground" htmlFor="localidad">Localidad</label>
              <input
                id="localidad"
                name="localidad"
                type="text"
                value={filtros.localidad}
                onChange={handleFiltroChange}
                className="rounded-md border px-3 py-2 w-full bg-background border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-semibold mb-1 text-muted-foreground" htmlFor="telefono">Teléfono</label>
              <input
                id="telefono"
                name="telefono"
                type="text"
                value={filtros.telefono}
                onChange={handleFiltroChange}
                className="rounded-md border px-3 py-2 w-full bg-background border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          {/* Selector de tipo de búsqueda */}
          <div className="flex items-center gap-2 mt-2">
            <label className="text-xs font-semibold text-muted-foreground">Tipo de búsqueda:</label>
            <select
              className="rounded-md border px-2 py-1 bg-background border-border text-foreground"
              value={searchType}
              onChange={e => setSearchType(e.target.value as 'contains' | 'startsWith')}
            >
              <option value="contains">Contiene</option>
              <option value="startsWith">Empieza con</option>
            </select>
          </div>
        </div>
      )}
      {isLoading && <div className="text-muted-foreground">Cargando clientes...</div>}
      {isError && <div className="text-destructive">Error al cargar clientes.</div>}
      {data && data.data.length === 0 && <div className="text-muted-foreground">No hay clientes registrados.</div>}
      {data && data.data.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg shadow max-w-5xl mx-auto">
            <table className="min-w-full bg-card text-card-foreground">
              <thead>
                <tr>
                  <th className="px-4 py-2 border-b border-border">Documento</th>
                  <th className="px-4 py-2 border-b border-border">Nombre</th>
                  <th className="px-4 py-2 border-b border-border">Apellido</th>
                  <th className="px-4 py-2 border-b border-border">Teléfono</th>
                  <th className="px-4 py-2 border-b border-border">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((cliente) => (
                  <tr key={cliente.id} className="hover:bg-muted/50 transition">
                    <td className="px-4 py-2 border-b border-border">{cliente.documento}</td>
                    <td className="px-4 py-2 border-b border-border">{cliente.nombre}</td>
                    <td className="px-4 py-2 border-b border-border">{cliente.apellido}</td>
                    <td className="px-4 py-2 border-b border-border">{cliente.telefono}</td>
                    <td className="px-4 py-2 border-b border-border flex gap-2">
                      <button
                        className="text-primary underline hover:text-primary/80"
                        onClick={() => setClienteSeleccionado(cliente)}
                      >
                        Ver detalles
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Controles de paginación */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              className="px-3 py-1 rounded bg-muted text-foreground border border-border font-semibold disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </button>
            <span className="text-sm font-semibold">Página {page} de {totalPages}</span>
            <button
              className="px-3 py-1 rounded bg-muted text-foreground border border-border font-semibold disabled:opacity-50"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente
            </button>
          </div>
        </>
      )}
      {clienteSeleccionado && (
        <ClienteDetalleModal
          cliente={clienteSeleccionado}
          onClose={() => setClienteSeleccionado(null)}
        />
      )}
      {clienteEditar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card dark:bg-[#181A20] text-card-foreground dark:text-white rounded-lg shadow-lg max-w-xl w-full p-8 relative border border-border max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-4 right-4 text-xl text-muted-foreground hover:text-destructive"
              onClick={() => setClienteEditar(null)}
              aria-label="Cerrar"
            >
              ×
            </button>
            <ClienteForm />
            {/* Aquí podrías pasar los datos del cliente a editar si el formulario soporta edición */}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClienteList; 